import type { PortfolioClient } from '@/data/portfolio.types';

/**
 * LOW PIPELINE (PLAN 8.4). The numbers are computed ONCE on the server (backend utils/content-cover.ts: planned +
 * ready days ahead, brand timezone, cover <= 14 days). Every Portfolio view reads these fields through this helper;
 * nothing here recomputes cover, so Grid / Board / Timeline / Workload / drawer can never disagree.
 */

type CoverFields = Pick<PortfolioClient, 'healthOverall' | 'lowPipeline' | 'runsDryOn' | 'coverDays' | 'coverBreakdown'>;

/** Brand shows the "Low pipeline" signal. Paused brands never do (server returns false for them too). */
export function isLowPipeline(client: CoverFields): boolean {
  return client.healthOverall !== 'paused' && client.lowPipeline;
}

export function countLowPipeline(clients: CoverFields[]): number {
  return clients.filter(isLowPipeline).length;
}

/** Planned days and ready days for the runway bar (the same split the server's cover is made of). */
export function pipelineBarDays(client: CoverFields): { scheduledDays: number; libraryDays: number } {
  return { scheduledDays: client.coverBreakdown.plannedDays, libraryDays: client.coverBreakdown.readyDays };
}

/**
 * "Oct 28" for the server's yyyy-MM-dd day key (already a brand-timezone calendar day, not an instant), so it is
 * formatted in UTC to keep the day exactly as the server said it. Empty when the brand has no dry date.
 */
export function formatRunsDryOn(dayKey: string | null, locale: string): string {
  if (!dayKey) return '';
  // Intentional exception to the useTimezone rule: a date-only key is a calendar day, not an instant, so applying the
  // user's timezone could shift it to the neighbouring day. UTC + noon keeps the day exactly as the server said it.
  return new Date(`${dayKey}T12:00:00Z`).toLocaleDateString(locale, { month: 'short', day: 'numeric', timeZone: 'UTC' });
}
